'use client'

/**
 * Global Error Boundary
 *
 * Catches unhandled errors in the app and displays a user-friendly message.
 * This prevents the entire app from crashing.
 * Reports errors to Sentry for tracking.
 *
 * Note: This does NOT catch errors in:
 * - The root layout (use global-error.tsx for that)
 * - Server components during build time
 */

import { useEffect } from 'react'
import * as Sentry from '@sentry/nextjs'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import Link from 'next/link'
import { Button } from '@/components/ui/Button'

/** Detect Next.js chunk load failures (e.g. stale deployment, network blip) */
function isChunkLoadError(error: Error): boolean {
  const msg = error.message ?? ''
  return (
    msg.includes('Failed to load chunk') ||
    msg.includes('Loading chunk') ||
    msg.includes('Loading CSS chunk') ||
    /ChunkLoadError|Loading chunk \d+ failed/.test(msg)
  )
}

interface ErrorProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function GlobalError({ error, reset }: ErrorProps) {
  const isChunkError = isChunkLoadError(error)

  useEffect(() => {
    // Keep a local console trace for debugging without double-reporting to Sentry.
    console.error('Global error boundary caught:', error)

    // Report to Sentry with error digest for correlation
    Sentry.captureException(error, {
      tags: {
        errorBoundary: 'global',
        digest: error.digest,
        isChunkLoadError: String(isChunkError),
      },
    })
  }, [error, isChunkError])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {/* Icon */}
        <div className="flex justify-center">
          <div className="bg-destructive/10 rounded-full p-4">
            <AlertTriangle className="w-12 h-12 text-destructive" />
          </div>
        </div>

        {/* Message */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            Something went wrong
          </h1>
          <p className="text-muted-foreground">
            {isChunkError
              ? 'A page failed to load. Refreshing will fetch the latest version.'
              : "We encountered an unexpected error. Don't worry, your data is safe."}
          </p>
        </div>

        {/* Error details (development only) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="bg-muted rounded-lg p-4 text-left">
            <p className="text-xs font-mono text-muted-foreground break-all">
              {error.message}
            </p>
            {error.digest && (
              <p className="text-xs font-mono text-muted-foreground mt-2">
                Digest: {error.digest}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => (isChunkError ? window.location.reload() : reset())}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {isChunkError ? 'Refresh page' : 'Try again'}
          </Button>
          <Button variant="outline" asChild>
            <Link href="/" className="gap-2">
              <Home className="w-4 h-4" />
              Go home
            </Link>
          </Button>
        </div>

        {/* Support link */}
        <p className="text-xs text-muted-foreground">
          If this keeps happening, please{' '}
          <a
            href="mailto:support@dday.io"
            className="text-primary hover:underline"
          >
            contact support
          </a>
          {error.digest && (
            <span className="block mt-1">Error ID: {error.digest}</span>
          )}
        </p>
      </div>
    </div>
  )
}
